import React, { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Palette, Trash2, Edit3, X } from 'lucide-react';

export default function BrandKitsPage() {
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', colors: ['#CDFE65', '#08080A', '#FFFFFF'], tone: 'premium', guidelines: '' });

  const load = async () => {
    try { setKits(await api.listBrandKits()); } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: '', colors: ['#CDFE65', '#08080A', '#FFFFFF'], tone: 'premium', guidelines: '' }); setShowModal(true); };
  const openEdit = (kit) => { setEditing(kit); setForm({ name: kit.name, colors: kit.colors || [], tone: kit.tone || 'premium', guidelines: kit.guidelines || '' }); setShowModal(true); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (editing) { await api.updateBrandKit(editing.id, form); toast.success('Brand kit updated'); }
      else { await api.createBrandKit(form); toast.success('Brand kit created'); }
      setShowModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this brand kit?')) return;
    try { await api.deleteBrandKit(id); toast.success('Deleted'); load(); } catch { toast.error('Failed to delete'); }
  };

  const updateColor = (idx, val) => { const c = [...form.colors]; c[idx] = val; setForm(p => ({ ...p, colors: c })); };
  const addColor = () => { if (form.colors.length < 10) setForm(p => ({ ...p, colors: [...p.colors, '#888888'] })); };
  const removeColor = (idx) => { setForm(p => ({ ...p, colors: p.colors.filter((_, i) => i !== idx) })); };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CDFE65] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-in-up opacity-0" data-testid="brandkits-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Chivo' }}>Brand <span className="text-[#7C3AED]">Kits</span></h1>
          <p className="text-white/40 mt-1">Keep every generation on-brand</p>
        </div>
        <button onClick={openCreate} className="btn-primary text-sm flex items-center gap-2" data-testid="create-brandkit-btn">
          <Plus className="w-4 h-4" /> New Kit
        </button>
      </div>

      {kits.length === 0 ? (
        <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-16 text-center">
          <Palette className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 mb-4">No brand kits yet. Create one to keep your generations on-brand.</p>
          <button onClick={openCreate} className="btn-primary text-sm" data-testid="empty-create-btn">Create Brand Kit</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {kits.map(kit => (
            <div key={kit.id} className="bg-[#0F0F11] border border-white/5 rounded-xl p-6 card-hover" data-testid={`brandkit-${kit.id}`}>
              <div className="flex items-start justify-between mb-4">
                <h3 className="font-bold text-lg">{kit.name}</h3>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(kit)} className="p-1.5 text-white/30 hover:text-[#CDFE65] transition-colors" data-testid={`edit-kit-${kit.id}`}><Edit3 className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(kit.id)} className="p-1.5 text-white/30 hover:text-red-400 transition-colors" data-testid={`delete-kit-${kit.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="flex gap-2 mb-3">
                {(kit.colors || []).map((c, i) => (
                  <div key={i} className="w-8 h-8 rounded-full border border-white/10" style={{ background: c }} title={c} />
                ))}
              </div>
              <div className="flex items-center gap-2 text-xs text-white/30 font-mono">
                <span className="px-2 py-0.5 bg-white/5 rounded">{kit.tone}</span>
                {kit.guidelines && <span className="truncate">{kit.guidelines.slice(0, 40)}...</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="brandkit-modal">
          <div className="bg-[#0F0F11] border border-white/10 rounded-2xl p-6 w-full max-w-md mx-4 animate-fade-in-up opacity-0">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">{editing ? 'Edit' : 'Create'} Brand Kit</h2>
              <button onClick={() => setShowModal(false)} className="text-white/30 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Name</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="forge-input w-full" placeholder="My Brand" data-testid="brandkit-name-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Colors</label>
                <div className="flex flex-wrap gap-2">
                  {form.colors.map((c, i) => (
                    <div key={i} className="relative group">
                      <input type="color" value={c} onChange={e => updateColor(i, e.target.value)} className="w-10 h-10 rounded-full cursor-pointer border border-white/10" />
                      <button onClick={() => removeColor(i)} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-[8px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">x</button>
                    </div>
                  ))}
                  <button onClick={addColor} className="w-10 h-10 rounded-full border border-dashed border-white/20 flex items-center justify-center text-white/30 hover:text-white hover:border-white/40 transition-colors">+</button>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Tone</label>
                <select value={form.tone} onChange={e => setForm(p => ({ ...p, tone: e.target.value }))} className="forge-input w-full" data-testid="brandkit-tone-select">
                  {['premium', 'playful', 'minimal', 'bold', 'warm', 'tech'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Guidelines</label>
                <textarea value={form.guidelines} onChange={e => setForm(p => ({ ...p, guidelines: e.target.value }))} rows={3} className="forge-input w-full h-auto py-3 resize-none" placeholder="Brand voice, style notes..." data-testid="brandkit-guidelines-input" />
              </div>
              <button onClick={handleSave} className="btn-primary w-full text-sm" data-testid="brandkit-save-btn">
                {editing ? 'Update' : 'Create'} Brand Kit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
